import { makeAutoObservable, runInAction } from './mobx-config';
import { logger } from '../../utils/debug/logger.js';

// Tailles ISO standard en mm (largeur x hauteur en orientation portrait)
const PAPER_SIZES = {
    'A0': { width: 841, height: 1189 },
    'A1': { width: 594, height: 841 },
    'A2': { width: 420, height: 594 },
    'A3': { width: 297, height: 420 },
    'A4': { width: 210, height: 297 }
};

// Marges d'impression en mm (pour le frame dans la page)
const PRINT_MARGINS = {
    top: 10,
    right: 15,
    bottom: 10,
    left: 15
};

// Clé localStorage pour la persistance
const STORAGE_KEY = 'geneafan_pdf_preferences';

class PdfConfigStore {
    config = {
        paperFormat: 'A3',
        orientation: 'landscape'
    };

    constructor() {
        makeAutoObservable(this);
        this.loadFromStorage();
    }

    /**
     * Charge les préférences depuis localStorage
     */
    loadFromStorage() {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                runInAction(() => {
                    if (parsed.paperFormat && PAPER_SIZES[parsed.paperFormat]) {
                        this.config.paperFormat = parsed.paperFormat;
                    }
                    if (parsed.orientation && ['portrait', 'landscape'].includes(parsed.orientation)) {
                        this.config.orientation = parsed.orientation;
                    }
                });
                logger.ui.debug('📄 Préférences PDF chargées:', this.config);
            }
        } catch (e) {
            logger.ui.warn('Erreur chargement préférences PDF:', e);
        }
    }

    /**
     * Sauvegarde les préférences dans localStorage
     */
    saveToStorage() {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config));
        } catch (e) {
            logger.ui.warn('Erreur sauvegarde préférences PDF:', e);
        }
    }

    /**
     * Définit le format de papier
     * @param {string} format - 'A0', 'A1', 'A2', 'A3', 'A4'
     */
    setPaperFormat(format) {
        if (PAPER_SIZES[format]) {
            runInAction(() => {
                this.config.paperFormat = format;
            });
            this.saveToStorage();
            logger.ui.debug(`📄 Format papier: ${format}`);
        }
    }

    /**
     * Définit l'orientation
     * @param {string} orientation - 'portrait' ou 'landscape'
     */
    setOrientation(orientation) {
        if (['portrait', 'landscape'].includes(orientation)) {
            runInAction(() => {
                this.config.orientation = orientation;
            });
            this.saveToStorage();
            logger.ui.debug(`🔄 Orientation: ${orientation}`);
        }
    }

    /**
     * Retourne la configuration actuelle
     */
    get getConfig() {
        return this.config;
    }

    /**
     * Retourne les dimensions du papier en mm (selon orientation)
     */
    get paperDimensions() {
        const size = PAPER_SIZES[this.config.paperFormat];
        if (this.config.orientation === 'landscape') {
            return { width: size.height, height: size.width };
        }
        return { width: size.width, height: size.height };
    }

    /**
     * Retourne les dimensions du frame en mm (papier - marges)
     */
    get frameDimensions() {
        const paper = this.paperDimensions;
        return {
            width: paper.width - PRINT_MARGINS.left - PRINT_MARGINS.right,
            height: paper.height - PRINT_MARGINS.top - PRINT_MARGINS.bottom
        };
    }

    /**
     * Retourne les marges d'impression
     */
    get printMargins() {
        return PRINT_MARGINS;
    }

    /**
     * Retourne les formats de papier disponibles
     */
    get availableFormats() {
        return Object.keys(PAPER_SIZES);
    }

    /**
     * Retourne une description lisible des dimensions actuelles
     */
    get dimensionsLabel() {
        const frame = this.frameDimensions;
        return `${frame.width} × ${frame.height} mm`;
    }
}

const pdfConfigStore = new PdfConfigStore();

export { PAPER_SIZES, PRINT_MARGINS };
export default pdfConfigStore;
